/* eslint-disable @typescript-eslint/no-unused-vars */
/* ─── ProjectHub hoofdbestand: login, topbalk, paneellayout ──────────────── */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { getGraphClient } from "./graphClient";
import type { Project, Plan, Bucket, PlannerTask, CheckItem } from "./types";
import { COLORS, SP_SITES, MIN_PANEL_PX } from "./config";
import { gQ } from "./graphApi";
import { I, extractProjectNumber, ResizeDivider, plannerTeamsUrl } from "./shared";
import { PlannerBoard } from "./PlannerPanel";
import { FilesPanel } from "./FilesPanel";
import { OutlookPanel } from "./OutlookPanel";
import { OneNotePanel } from "./OneNotePanel";
import { AgendaPanel } from "./AgendaPanel";

/* ─── Main App ───────────────────────────────────────────────────────────── */
function App(){
  const{instance}=useMsal();const isAuth=useIsAuthenticated();
  const[un,sUn]=useState("");const[projects,sPr]=useState<Project[]>([]);const[ai,sAi]=useState(0);
  const[ld,sLd]=useState(false);const[err,sErr]=useState("");const[gc,sGc]=useState<any>(null);

  // ── Paneelinstellingen laden uit localStorage ─────────────────────────────
  // Slaat per gebruiker op: welke panelen actief, volgorde, breedtes
  const STORAGE_KEY="projecthub_panels_v1";
  const loadPanelSettings=()=>{
    try{
      const raw=localStorage.getItem(STORAGE_KEY);
      if(raw)return JSON.parse(raw);
    }catch(e){}
    return null;
  };
  const savedSettings=loadPanelSettings();

  const[panels,sPa]=useState<{planner:boolean;files:boolean;outlook:boolean;onenote:boolean;agenda:boolean}>(
    savedSettings?.panels||{planner:true,files:true,outlook:false,onenote:false,agenda:false}
  );
  const ALL_PANELS=["planner","files","outlook","onenote","agenda"] as const;
  type PanelKey=typeof ALL_PANELS[number];
  const[panelOrder,setPanelOrder]=useState<PanelKey[]>(
    savedSettings?.panelOrder||[...ALL_PANELS]
  );
  const panelDragRef=useRef<PanelKey|null>(null);
  const[panelWidths,sPW]=useState<number[]>(savedSettings?.panelWidths||[700,400]);
  const[activeSiteIdx,setActiveSiteIdx]=useState<number>(savedSettings?.activeSiteIdx||0);
  const containerRef=useRef<HTMLDivElement>(null);

  // Sla paneelinstellingen op bij elke wijziging
  useEffect(()=>{
    try{
      localStorage.setItem(STORAGE_KEY,JSON.stringify({panels,panelOrder,panelWidths,activeSiteIdx}));
    }catch(e){}
  },[panels,panelOrder,panelWidths,activeSiteIdx]);

  const login=()=>{instance.loginRedirect(loginRequest).catch(()=>sErr("Login mislukt."))};

  const fetchP=useCallback(async(c:any)=>{
    sLd(true);sErr("");
    try{
      const me=await gQ(()=>c.api("/me").get());
      sUn(me.displayName);

      // ── Gepinde plans ophalen via beta/me/planner ───────────────────────
      let favPlanIds: string[] = [];
      try {
        const plannerUser = await gQ(()=>c.api("/me/planner").version("beta").get());
        const refs = plannerUser.favoritePlanReferences || {};
        favPlanIds = Object.keys(refs);
      } catch(e) {
        console.warn("beta/me/planner niet beschikbaar:", e);
      }

      if(favPlanIds.length === 0) {
        const atr = await gQ(()=>c.api("/me/planner/tasks").top(200).get());
        const taskPlanIds = new Set<string>((atr.value||[]).map((t:any)=>t.planId as string));
        const teamsResp = await gQ(()=>c.api("/me/joinedTeams").get());
        for(const team of (teamsResp.value||[])){
          try{const pr=await gQ(()=>c.api(`/groups/${team.id}/planner/plans`).get());for(const p of(pr.value||[]))taskPlanIds.add(p.id)}catch(e){}
        }
        favPlanIds = Array.from(taskPlanIds);
      }

      // Bewaar bestaande checklists per task-id zodat we ze niet opnieuw hoeven laden
      // Bij een refresh zonder veranderingen vermijden we zo honderden API-calls.
      const existingChecklists = new Map<string, CheckItem[]>();
      sPr(prev => {
        for (const proj of prev) {
          for (const t of proj.tasks) {
            if (t.checklist && t.checklist.length > 0) {
              existingChecklists.set(t.id, t.checklist);
            }
          }
        }
        return prev;
      });

      // ── Fase 1: basisdata parallel laden (plan + buckets + taken) ───────
      // Geen checklist-details in deze fase — dat zijn honderden API-calls.
      const pl: Project[] = [];
      await Promise.all(favPlanIds.map(async(pid,idx)=>{
        try{
          const [pd, br, tr] = await Promise.all([
            gQ(()=>c.api(`/planner/plans/${pid}`).get()),
            gQ(()=>c.api(`/planner/plans/${pid}/buckets`).get()),
            gQ(()=>c.api(`/planner/plans/${pid}/tasks`).top(200).get()),
          ]);
          const bk: Bucket[]=(br.value||[]).map((b:any)=>({id:b.id,name:b.name,planId:b.planId,orderHint:b.orderHint}));
          // Taken: hergebruik bestaande checklist als die er is, anders lege array
          const ts: PlannerTask[]=(tr.value||[]).map((t:any)=>({
            id:t.id,title:t.title,bucketId:t.bucketId,
            percentComplete:t.percentComplete,dueDateTime:t.dueDateTime,
            priority:t.priority,
            orderHint:t.orderHint||"",
            checklist: existingChecklists.get(t.id) || []
          }));
          let gn="Persoonlijk";
          try{const g=await gQ(()=>c.api(`/groups/${pd.owner}`).select("displayName").get());gn=g.displayName}catch(e){}
          pl[idx]={
            group:{id:pd.owner,displayName:gn},
            plan:{id:pd.id,title:pd.title,owner:pd.owner},
            tasks:ts,buckets:bk,
            color:COLORS[idx%COLORS.length],
            projectNumber:extractProjectNumber(pd.title)
          };
        }catch(e){console.warn("Plan overgeslagen:",pid,e)}
      }));

      const validPl=pl.filter(Boolean);
      sPr(validPl);
      if(validPl.length>0&&ai>=validPl.length)sAi(0);
      sLd(false); // UI direct zichtbaar, nog geen checklists

      // ── Fase 2: checklists laden voor taken die er nog GEEN hebben ──────
      // Gespreid laden om throttling te vermijden. gQ zorgt voor rate-limiting.
      // Taken met een al gecachede checklist worden overgeslagen.
      for(const proj of validPl){
        const tasks=proj.tasks.filter(t=>
          t.percentComplete<100 && t.checklist.length===0 && !existingChecklists.has(t.id)
        );
        if(tasks.length===0)continue;
        const BATCH=4;
        for(let i=0;i<tasks.length;i+=BATCH){
          const chunk=tasks.slice(i,i+BATCH);
          const results=await Promise.allSettled(
            chunk.map(t=>gQ(()=>c.api(`/planner/tasks/${t.id}/details`).get()))
          );
          results.forEach((r,j)=>{
            if(r.status==="fulfilled"&&r.value.checklist){
              const cl:CheckItem[]=Object.entries(r.value.checklist).map(([id,v]:[string,any])=>({id,title:v.title,isChecked:v.isChecked}));
              if(cl.length>0){
                // Update de taak in state met checklist
                sPr(prev=>prev.map(p=>p.plan.id===proj.plan.id
                  ?{...p,tasks:p.tasks.map(t=>t.id===chunk[j].id?{...t,checklist:cl}:t)}
                  :p
                ));
              }
            }
          });
          // gQ regelt throttling al, korte pauze tussen batches voor zekerheid
          if(i+BATCH<tasks.length)await new Promise(r=>setTimeout(r,150));
        }
      }
    }catch(e:any){sErr("Fout: "+(e.message||""));sLd(false);}
  },[ai]);

  useEffect(()=>{if(!isAuth)return;const c=getGraphClient(instance as any);sGc(c);fetchP(c)},[isAuth,instance,fetchP]);  const refresh=useCallback(()=>{if(gc)fetchP(gc)},[gc,fetchP]);

  // ── Resize logica met pixel-breedtes ──────────────────────────────────────
  // Strategie: alle panelen behalve het laatste krijgen een vaste pixel-breedte.
  // Het laatste paneel heeft flex:1 en vult automatisch de rest op.
  // De divider past alleen de breedte aan van het paneel LINKS van de divider.
  const handleResize=(dividerIdx:number, dx:number)=>{
    if(!containerRef.current)return;
    sPW(prev=>{
      const next=[...prev];
      const totalW=containerRef.current!.offsetWidth;
      const activePanelCount=Object.values(panels).filter(Boolean).length;
      const maxW=totalW-(activePanelCount-1)*MIN_PANEL_PX;
      // dividerIdx is de index van het paneel RECHTS van de divider
      // we passen het paneel LINKS aan (dividerIdx - 1)
      const leftIdx=dividerIdx-1;
      const rightIdx=dividerIdx;
      const newLeft=Math.max(MIN_PANEL_PX,Math.min(maxW,(next[leftIdx]||400)+dx));
      const diff=newLeft-(next[leftIdx]||400);
      next[leftIdx]=newLeft;
      // Pas ook het rechter paneel aan zodat de totale breedte stabiel blijft
      next[rightIdx]=Math.max(MIN_PANEL_PX,(next[rightIdx]||400)-diff);
      return next;
    });
  };

  // Reset breedtes als het aantal panelen verandert — maar alleen als er geen opgeslagen breedte is
  useEffect(()=>{
    if(savedSettings?.panelWidths)return; // bewaar opgeslagen breedtes
    const n=Object.values(panels).filter(Boolean).length;
    const w=containerRef.current?.offsetWidth||1200;
    if(n===1)sPW([w]);
    else if(n===2)sPW([Math.round(w*0.6)]);
    else if(n===3)sPW([Math.round(w*0.45),Math.round(w*0.30)]);
    else sPW([Math.round(w*0.40),Math.round(w*0.25),Math.round(w*0.20)]);
  },[panels.planner,panels.files,panels.outlook,panels.onenote,panels.agenda]);// eslint-disable-line

  if(!isAuth)return<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"Segoe UI",background:"#F3F3F3"}}><h1 style={{color:"#0078D4",fontSize:32,fontWeight:800,marginBottom:8}}>ProjectHub</h1><p style={{color:"#5F6368",marginBottom:24}}>Log in met je Microsoft 365-account</p><button onClick={login} style={{background:"#0078D4",color:"#fff",border:"none",borderRadius:6,padding:"12px 32px",fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"Segoe UI"}}>Inloggen met Microsoft</button></div>;
  if(ld&&projects.length===0)return<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"Segoe UI",background:"#F3F3F3"}}><h1 style={{color:"#0078D4",fontSize:24,fontWeight:800,marginBottom:8}}>ProjectHub</h1><p style={{color:"#5F6368"}}>Laden... (plans ophalen uit Planner favorieten)</p></div>;

  const p=projects[ai]||null;
  const activePanelKeys=Object.entries(panels).filter(([_,v])=>v).map(([k])=>k);

  // ── Paneel-elementen bouwen op basis van panelOrder ────────────────────
  const panelDefs:[string,React.ReactNode][]=[]; // [key, content]

  const panelContent:(k:string)=>React.ReactNode|null=(k)=>{
    if(k==="planner"&&panels.planner&&p) return <>
      <a
        href={`${plannerTeamsUrl(p.plan.id)}`}
        title="Open dit plan in Teams"
        style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderBottom:"1px solid #E0E0E0",background:"#F0F0F0",flexShrink:0,textDecoration:"none",color:"#1A1A1A",cursor:"pointer"}}
        onMouseEnter={e=>{e.currentTarget.style.background="#E8E8E8";}}
        onMouseLeave={e=>{e.currentTarget.style.background="#F0F0F0";}}
      >
        <I.Board/><span style={{fontSize:11,fontWeight:700}}>Planner</span><span style={{opacity:.4,display:"flex",alignItems:"center"}}><I.Ext/></span><span style={{marginLeft:"auto",fontSize:10,color:"#9AA0A6"}}>{p.tasks.filter(t=>t.percentComplete<100).length} actief</span>
      </a>
      <div style={{flex:1,padding:10,display:"flex",flexDirection:"column",minHeight:0}}>{gc&&<PlannerBoard project={p} graphClient={gc} onRefresh={refresh}/>}</div>
    </>;
    if(k==="files"&&panels.files&&gc&&p) return <>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderBottom:"1px solid #E0E0E0",background:"#F0F0F0",flexShrink:0}}>
        <I.Files/><span style={{fontSize:11,fontWeight:700}}>Bestanden</span></div>
      <div style={{flex:1,padding:8,display:"flex",flexDirection:"column",minHeight:0}}><FilesPanel graphClient={gc} sites={SP_SITES} projectNumber={p.projectNumber} activeSiteIdx={activeSiteIdx} onSiteChange={setActiveSiteIdx}/></div>
    </>;
    if(k==="outlook"&&panels.outlook&&gc&&p) return <>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderBottom:"1px solid #E0E0E0",background:"#F0F0F0",flexShrink:0}}>
        <I.Mail/><span style={{fontSize:11,fontWeight:700}}>Outlook</span></div>
      <div style={{flex:1,display:"flex",flexDirection:"column",minHeight:0}}><OutlookPanel graphClient={gc} projectNumber={p.projectNumber} color={p.color}/></div>
    </>;
    if(k==="onenote"&&panels.onenote&&gc&&p) return <>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderBottom:"1px solid #E0E0E0",background:"#F0F0F0",flexShrink:0}}>
        <I.Note/><span style={{fontSize:11,fontWeight:700}}>OneNote</span></div>
      <div style={{flex:1,display:"flex",flexDirection:"column",minHeight:0}}><OneNotePanel graphClient={gc} projectNumber={p.projectNumber} color={p.color} site={SP_SITES[activeSiteIdx]||SP_SITES[0]}/></div>
    </>;
    if(k==="agenda"&&panels.agenda&&gc) return <>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 11px",borderBottom:"1px solid #E0E0E0",background:"#F0F0F0",flexShrink:0}}>
        <I.Clock/><span style={{fontSize:11,fontWeight:700}}>Agenda</span></div>
      <div style={{flex:1,display:"flex",flexDirection:"column",minHeight:0}}><AgendaPanel graphClient={gc} color={p?.color||"#0078D4"}/></div>
    </>;
    return null;
  };

  for(const k of panelOrder){
    const content=panelContent(k);
    if(content)panelDefs.push([k,content]);
  }

  // Bouw de uiteindelijke JSX-rij met dividers
  const panelElements:React.ReactNode[]=[];
  panelDefs.forEach(([key,content],i)=>{
    // Alle panelen krijgen een vaste pixel-breedte via width.
    // Het laatste paneel gebruikt ook panelWidths maar met flex:1 als fallback
    // zodat het de resterende ruimte opvult als de som kleiner is dan de container.
    const isLast=i===panelDefs.length-1;
    const w=panelWidths[i]||400;
    const style:React.CSSProperties={
      width:w,
      flexShrink:0,
      flexGrow:isLast?1:0, // laatste paneel mag groeien maar heeft ook vaste basis
      display:"flex",flexDirection:"column",
      background:"#fff",borderRadius:8,
      border:"1px solid #E0E0E0",overflow:"hidden",
      boxShadow:"0 1px 3px rgba(0,0,0,0.04)",
      minWidth:MIN_PANEL_PX
    };

    if(i>0){
      panelElements.push(<ResizeDivider key={`div-${i}`} onResize={dx=>handleResize(i,dx)}/>);
    }
    panelElements.push(<div key={key} style={style}>{content}</div>);
  });

  return<div style={{height:"100vh",width:"100vw",display:"flex",flexDirection:"column",fontFamily:"Segoe UI,system-ui,sans-serif",overflow:"hidden",background:"#F3F3F3",color:"#1A1A1A"}}>
    {/* ── Topbalk ── */}
    <div style={{display:"flex",alignItems:"center",padding:"0 14px",height:46,flexShrink:0,borderBottom:"1px solid #E0E0E0",background:"#fff",gap:8}}>
      <div style={{fontSize:13,fontWeight:800,marginRight:4}}><span style={{color:p?.color||"#0078D4",marginRight:3}}>{"\u25A0"}</span>ProjectHub</div>
      <div style={{width:1,height:20,background:"#E0E0E0"}}/>
      {/* Project-tabs */}
      <div style={{display:"flex",alignItems:"center",gap:2,overflowX:"auto",flex:1,padding:"2px 0"}}>
        {projects.map((pr,i)=>(
          <button key={pr.plan.id} onClick={()=>sAi(i)} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:5,border:i===ai?`1.5px solid ${pr.color}`:"1.5px solid transparent",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"Segoe UI",background:i===ai?pr.color+"10":"transparent",color:i===ai?pr.color:"#5F6368",whiteSpace:"nowrap",flexShrink:0}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:pr.color}}/>
            {pr.plan.title}
          </button>
        ))}
        {projects.length===0&&!ld&&(
          <span style={{fontSize:11,color:"#9AA0A6",fontStyle:"italic"}}>
            Pin plans in Planner om ze hier te zien
          </span>
        )}
      </div>
      {/* Panelen-knoppen – draggable voor volgorde */}
      <div style={{display:"flex",gap:3,marginRight:8,alignItems:"center"}}>
        <span style={{fontSize:9,color:"#9AA0A6",fontWeight:600,textTransform:"uppercase",marginRight:2}}>Panelen</span>
        {panelOrder.map(k=>{
          const meta:{l:string;i:React.ReactNode}={
            planner:{l:"Planner",i:<I.Board/>},
            files:{l:"Bestanden",i:<I.Files/>},
            outlook:{l:"Outlook",i:<I.Mail/>},
            onenote:{l:"OneNote",i:<I.Note/>},
            agenda:{l:"Agenda",i:<I.Clock/>},
          }[k];
          const active=panels[k];
          return (
            <button
              key={k}
              draggable
              onDragStart={()=>{panelDragRef.current=k;}}
              onDragOver={e=>{
                e.preventDefault();
                const drag=panelDragRef.current;
                if(drag&&drag!==k){
                  setPanelOrder(prev=>{
                    const f=prev.indexOf(drag),t=prev.indexOf(k);
                    if(f===-1||t===-1)return prev;
                    const n=[...prev];n.splice(f,1);n.splice(t,0,drag);return n;
                  });
                }
              }}
              onDragEnd={()=>{panelDragRef.current=null;}}
              onClick={()=>sPa(pv=>({...pv,[k]:!pv[k]}))}
              style={{display:"flex",alignItems:"center",gap:3,padding:"3px 8px",borderRadius:4,border:active?"1px solid #0078D4":"1px solid #E0E0E0",background:active?"#0078D410":"transparent",color:active?"#0078D4":"#9AA0A6",cursor:"grab",fontSize:10,fontWeight:500,fontFamily:"Segoe UI",userSelect:"none"}}
            >
              {meta.i} {meta.l}
            </button>
          );
        })}
      </div>
      <button onClick={refresh} style={{display:"flex",alignItems:"center",gap:3,padding:"4px 8px",borderRadius:4,border:"1px solid #E0E0E0",background:"transparent",color:"#5F6368",cursor:"pointer",fontSize:10,fontFamily:"Segoe UI",flexShrink:0}}><I.Refresh/></button>
      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}><span style={{fontSize:10,color:"#9AA0A6"}}>{un}</span><button onClick={()=>instance.logoutRedirect()} style={{display:"flex",alignItems:"center",background:"none",border:"none",cursor:"pointer",padding:3,color:"#9AA0A6"}}><I.Logout/></button></div>
    </div>

    {/* ── Project-infobalk ── */}
    {p&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"5px 14px",background:"#fff",borderBottom:"1px solid #E8E8E8",flexShrink:0,fontSize:11}}>
      <span style={{width:8,height:8,borderRadius:"50%",background:p.color}}/>
      <span style={{fontWeight:700}}>{p.plan.title}</span>
      <span style={{color:"#9AA0A6"}}>({p.group.displayName})</span>
      <span style={{color:"#9AA0A6",display:"flex",alignItems:"center",gap:3}}><I.Link/> #{p.projectNumber}</span>
      <span style={{display:"flex",alignItems:"center",gap:3,fontSize:10,color:"#CA5010",marginLeft:4}}><I.Star/> gepind</span>
      <a href={`${plannerTeamsUrl(p.plan.id)}`} style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,color:"#9AA0A6",textDecoration:"none",marginLeft:"auto"}} onMouseEnter={e=>e.currentTarget.style.color=p.color} onMouseLeave={e=>e.currentTarget.style.color="#9AA0A6"}><I.Ext/> Planner</a>
    </div>}

    {/* ── Panelen ── */}
    <div ref={containerRef} style={{flex:1,display:"flex",gap:0,padding:10,overflow:"hidden"}}>
      {panelElements.length>0
        ?panelElements
        :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#9AA0A6",fontSize:13}}>Activeer panelen hierboven.</div>
      }
    </div>

    {/* ── Statusbalk ── */}
    <div style={{display:"flex",alignItems:"center",padding:"0 14px",height:24,flexShrink:0,borderTop:"1px solid #E0E0E0",background:"#fff",fontSize:9,color:"#9AA0A6",gap:10}}>
      <span>{projects.length} gepinde plans</span>
      {p&&<span>#{p.projectNumber}</span>}
      {p&&<span>{p.tasks.length} taken</span>}
      <span style={{marginLeft:"auto",opacity:.5}}>ProjectHub v3.1</span>
    </div>

    {err&&<div style={{position:"fixed",bottom:36,left:"50%",transform:"translateX(-50%)",background:"#FFF0F0",border:"1px solid #D13438",borderRadius:8,padding:"8px 16px",fontSize:12,color:"#D13438",boxShadow:"0 2px 8px rgba(0,0,0,0.1)",zIndex:100}}>{err}</div>}
  </div>
}

export default App;
